import React, { useState } from 'react';
import { FaWhatsapp, FaEnvelope, FaCopy, FaUser, FaUsers, FaExternalLinkAlt } from 'react-icons/fa';
import Modal from '../Modal/Modal';
import { showToast } from '../../utils/toast';
import './ShareChoiceModal.scss';

/**
 * Reusable share dialog: "To Client" (send directly) vs "To Others" (copy template).
 *
 * Props:
 * - isOpen, onClose
 * - channel: 'whatsapp' | 'email'
 * - title: modal title e.g. "Share Quotation via WhatsApp"
 * - docLabel: short label e.g. "QT-2026-0001 • John"
 * - clientName, clientContact (phone or email display string)
 * - hasClientContact: bool — false disables "send to client" with hint
 * - clientMessage: preview text for client (whatsapp text, or email body)
 * - othersMessage: preview text for others (whatsapp text, or email body)
 * - clientSubject / othersSubject: only for email channel
 * - onSendToClient: () => void (parent opens wa.me / mailto with client template)
 * - onShareOthersViaApp: () => void (parent opens blank wa.me / blank mailto with others template)
 */
const ShareChoiceModal = ({
  isOpen,
  onClose,
  channel = 'whatsapp',
  title = 'Share',
  docLabel = '',
  clientName = '',
  clientContact = '',
  hasClientContact = true,
  clientMessage = '',
  othersMessage = '',
  clientSubject = '',
  othersSubject = '',
  onSendToClient,
  onShareOthersViaApp,
}) => {
  const [tab, setTab] = useState('client');
  const isWA = channel === 'whatsapp';

  // Reset to client tab each time modal opens
  React.useEffect(() => {
    if (isOpen) setTab('client');
  }, [isOpen]);

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied — paste it anywhere to share`, { type: 'success' });
    } catch {
      // Clipboard API may be blocked — show text so user can copy manually
      showToast(text, { duration: 8000 });
    }
  };

  const handleCopyClient = () => {
    const text = isWA ? clientMessage : `Subject: ${clientSubject}\n\n${clientMessage}`;
    copyText(text, isWA ? 'Client message' : 'Client email');
  };

  const handleCopyOthers = () => {
    const text = isWA ? othersMessage : `Subject: ${othersSubject}\n\n${othersMessage}`;
    copyText(text, isWA ? 'Message template' : 'Email template');
  };

  const previewClient = isWA ? clientMessage : `Subject: ${clientSubject}\n\n${clientMessage}`;
  const previewOthers = isWA ? othersMessage : `Subject: ${othersSubject}\n\n${othersMessage}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="medium">
      <div className="share-choice">
        {docLabel ? <div className="share-choice__doc">{docLabel}</div> : null}

        <div className="share-choice__tabs" role="tablist">
          <button
            className={`share-choice__tab${tab === 'client' ? ' share-choice__tab--active' : ''}`}
            onClick={() => setTab('client')}
            type="button"
          >
            <FaUser /> To Client
          </button>
          <button
            className={`share-choice__tab${tab === 'others' ? ' share-choice__tab--active' : ''}`}
            onClick={() => setTab('others')}
            type="button"
          >
            <FaUsers /> To Others
          </button>
        </div>

        {tab === 'client' ? (
          <div className="share-choice__panel">
            <p className="share-choice__lead">
              Send directly to <strong>{clientName || 'the client'}</strong>
              {clientContact ? <span className="share-choice__contact"> ({clientContact})</span> : null} via{' '}
              <strong>{isWA ? 'WhatsApp' : 'Email'}</strong> with a personalized message.
            </p>
            {!hasClientContact ? (
              <div className="share-choice__warn">
                {isWA ? 'No phone number available for this client.' : 'No email address available for this client.'} You can still copy the message below and send it manually.
              </div>
            ) : null}
            <pre className="share-choice__preview">{previewClient || '—'}</pre>
            <div className="share-choice__actions">
              <button className="btn-secondary" onClick={handleCopyClient} type="button" title="Copy client message">
                <FaCopy /> Copy
              </button>
              <button
                className="btn-primary"
                onClick={() => { try { onSendToClient && onSendToClient(); } catch (e) { console.error(e); } }}
                disabled={!hasClientContact}
                type="button"
                title={isWA ? 'Open WhatsApp chat with client' : 'Open mail app addressed to client'}
              >
                {isWA ? <FaWhatsapp /> : <FaEnvelope />}
                {isWA ? ' Send to Client' : ' Email Client'}
              </button>
            </div>
          </div>
        ) : (
          <div className="share-choice__panel">
            <p className="share-choice__lead">
              Copy this neutral template and share it with <strong>anyone else</strong> — partner, family member, staff.
              It includes the client reference so the receiver has full context.
            </p>
            <pre className="share-choice__preview">{previewOthers || '—'}</pre>
            <div className="share-choice__actions">
              <button className="btn-primary" onClick={handleCopyOthers} type="button" title="Copy message template">
                <FaCopy /> Copy Message
              </button>
              <button
                className="btn-secondary"
                onClick={() => { try { onShareOthersViaApp && onShareOthersViaApp(); } catch (e) { console.error(e); } }}
                type="button"
                title={isWA ? 'Open WhatsApp share picker with this message' : 'Open mail app with this message'}
              >
                <FaExternalLinkAlt /> {isWA ? ' Open WhatsApp' : ' Open Email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ShareChoiceModal;
